/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import classnames from 'classnames';
import {dlsColor, dlsShadow} from './theme';
// eslint-disable-next-line ion-eslint-rules/fast-refresh-verifier
import {isLegacyTheme} from './useThemeContext';

/**
 * Provides a theme-aware style selector for non-reactive contexts.
 *
 * Returns a thunk that resolves the appropriate style value at call time,
 * based on whether the active theme is `legacy` or `current`.
 *
 * This helper is useful inside style factories (e.g. JSS `makeStyles`)
 * where values are resolved lazily and must respect runtime theme switching.
 *
 * ```typescript
 * import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
 *
 * const useStyles = makeStyles({
 *   inputRoot : {
 *     '&.Mui-focused::before' : {
 *       borderBottom : toggleThemeStyle('1px solid #02ABCA', 'none')
 *     }
 *   }
 * });
 * ```
 *
 * `T` may also be a whole style-rule object, giving an additive,
 * current-theme-only override class a legacy no-op branch in one call - the
 * legacy branch resolves to `{}` (no rules emitted), while the current
 * branch carries the real override:
 *
 * ```typescript
 * import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
 *
 * const useStyles = makeStyles(() => ({
 *   container : {height : '100vh'},
 *   // overrides container's height for current theme only ...
 *   currentContainer : toggleThemeStyle({}, {height : 'calc(100vh - 99px)'})
 * }));
 * ```
 *
 * @typeParam T The value type for the style branch (string/object/etc).
 * @param legacyStyle Style value to use when the active theme is legacy.
 * @param currentStyle Style value to use when the active theme is current.
 * @returns A function that resolves to `legacyStyle` or `currentStyle`
 *          based on `isLegacyTheme()`.
 */
export const toggleThemeStyle = <T>(legacyStyle: T, currentStyle: T): (() => T) =>
  (): T => (isLegacyTheme() ? legacyStyle : currentStyle)
;

/**
 * Resolves a value for current theme only, `undefined` for legacy - for use
 * *inside* a per-property dynamic JSS value that also depends on other
 * per-instance props (e.g. `({someProp}) => currentOnly(someProp ? 'a' : 'b')`).
 *
 * `toggleThemeStyle` can't cover this case: it wraps a whole rule (or a
 * whole property) in its own thunk, resolved once by JSS - a further
 * per-property dynamic function nested inside its returned branch is not
 * re-resolved against props, so it silently never re-evaluates. Calling
 * `currentOnly` from inside a property's own directly-authored function
 * avoids that, since the property's function itself is what JSS calls with
 * live props each time - `currentOnly` only picks the branch.
 *
 * ```typescript
 * import {currentOnly} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   container : {
 *     borderRadius : ({selectedType}: {selectedType?: string}) =>
 *       currentOnly(selectedType ? '0 0 12px 12px' : '12px')
 *   }
 * }));
 * ```
 *
 * @param currentValue Value to use for current theme.
 * @returns `currentValue` when the active theme is current, otherwise
 *          `undefined` (which JSS omits from the emitted rule).
 */
export const currentOnly = <T>(currentValue: T): T | undefined =>
  (isLegacyTheme() ? undefined : currentValue)
;

/**
 * Merges a base className with a mobile-only override className.
 *
 * Applies `mobileClassName` only when `isPhone` is `true`, pairing with a
 * `toggleThemeStyle({}, {...})`-wrapped mobile override class (see
 * `toggleThemeStyle` above). A nested `[theme.breakpoints.down('sm')]`
 * block declared inside a rule-level `toggleThemeStyle(...)` value is not
 * guaranteed to reach the browser, since the rule is resolved once at
 * style-definition time rather than through JSS's normal breakpoint
 * handling - defining the mobile override as its own class and toggling it
 * from the component sidesteps that entirely.
 *
 * ```typescript
 * import {currentMobileClassName, toggleThemeStyle} from '@ion/dls/src/theme/helpers';
 * import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   container : {height : '100vh'},
 *   // overrides container's height for current theme + mobile only ...
 *   containerMobile : toggleThemeStyle({}, {height : 'calc(100vh - 99px)'})
 * }));
 *
 * const classes  = useStyles();
 * const isPhone  = useIsPhone();
 * const rootClassName = currentMobileClassName(
 *   classes.container,
 *   classes.containerMobile,
 *   isPhone
 * );
 *
 * ```
 *
 * @param baseClassName   Always-applied base className.
 * @param mobileClassName ClassName applied only when `isPhone` is `true`.
 * @param isPhone         Whether the current viewport is phone-sized.
 * @returns               The merged className string.
 */
export const currentMobileClassName = (
  baseClassName: string,
  mobileClassName: string,
  isPhone: boolean
): string => classnames(baseClassName, {[mobileClassName]: isPhone});

/**
 * Merges a component's className with its current theme override - the
 * `current*` class is added only while the current theme is active, so the
 * legacy theme keeps exactly its base className.
 *
 * ```typescript
 * import {themedClassName} from '@ion/dls/src/theme/helpers';
 * import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
 * ...
 *
 * const classes     = useStyles();
 * const {isCurrent} = useThemeContext();
 *
 * <div className={themedClassName(
 *   classes.label,
 *   classes.currentLabel,
 *   isCurrent
 * )} />
 *
 * ```
 *
 * @param baseClassName    Always-applied base className.
 * @param currentClassName ClassName applied only when `isCurrent` is `true`.
 * @param isCurrent        Whether the current theme is active.
 * @returns                The merged className string.
 */
export const themedClassName = (
  baseClassName: string,
  currentClassName: string,
  isCurrent: boolean
): string => classnames(baseClassName, {[currentClassName]: isCurrent});

/**
 * Resolves the `marginTop` needed to collapse two adjacent 1px borders into
 * a single visible line.
 *
 * Pulls an element's own top border up onto whatever precedes it (a prior
 * sibling row, or a wrapping container's own border) so the two 1px lines
 * render as one instead of visibly doubling in thickness wherever they meet.
 *
 * ```typescript
 * import {collapseAdjacentBorderMargin} from '@ion/dls/src/theme/helpers';
 *
 * const useStyles = makeStyles(() => ({
 *   row : {
 *     border    : '1px solid #E0E0E0',
 *     marginTop : ({isStacked}: {isStacked: boolean}) =>
 *       collapseAdjacentBorderMargin(isStacked)
 *   }
 * }));
 * ```
 *
 * @param shouldCollapse Whether this element's own border sits directly
 *                       against another bordered element that needs
 *                       collapsing.
 * @returns `'-1px'` when `shouldCollapse` is `true`, otherwise `0`.
 */
export const collapseAdjacentBorderMargin = (shouldCollapse: boolean): string | number => (
  shouldCollapse ? '-1px' : 0
);

/**
 * Figma-specced alignment for a current-theme admin/report page's title-and-
 * -CTAs header row. Previously hand-duplicated (as an identical
 * `toggleThemeStyle('center', 'flex-end')` collapsed to a flat `'center'`,
 * since legacy already used `'center'`) across every such page.
 *
 * ```typescript
 * import {dlsPageHeaderCtaAlignItems} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   titleCtaHeader : {
 *     alignItems : dlsPageHeaderCtaAlignItems
 *   }
 * }));
 * ```
 */
export const dlsPageHeaderCtaAlignItems = 'center';

/**
 * Figma-specced line-height for a current-theme admin/report page's title
 * (43px, not the browser default 100%/32px). Previously hand-duplicated
 * across every such page.
 *
 * ```typescript
 * import {dlsPageHeaderTitleLineHeight} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   titleLabel : {
 *     lineHeight : dlsPageHeaderTitleLineHeight
 *   }
 * }));
 * ```
 */
export const dlsPageHeaderTitleLineHeight = toggleThemeStyle('inherit', '43px !important');

/**
 * Figma-specced vertical padding for a current-theme admin/report page's
 * sub-account toggle row (8px top/bottom). Previously hand-duplicated across
 * every such page.
 *
 * ```typescript
 * import {dlsSubAccountVerticalPadding} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   subAccount : {
 *     ...dlsSubAccountVerticalPadding,
 *     paddingLeft : toggleThemeStyle('35px', '0px')
 *   }
 * }));
 * ```
 */
export const dlsSubAccountVerticalPadding = {
  paddingTop    : toggleThemeStyle('0px', '8px'),
  paddingBottom : toggleThemeStyle('0px', '8px')
};

/**
 * Overrides the global `MuiFormHelperText.root` 16px left padding for an
 * error message only - current theme only, legacy keeps the global 16px.
 * Meant to be spread into a component's own `error`-only helper-text class
 * (only applied when the field is actually in error), never the base
 * helper-text class used for regular, non-error text.
 *
 * ```typescript
 * import {errorHelperTextPadding} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   errorText : {
 *     color : dlsColor('red'),
 *     ...errorHelperTextPadding
 *   }
 * }));
 * ```
 */
export const errorHelperTextPadding = {
  paddingLeft : toggleThemeStyle('16px !important', '0px !important')
};

/**
 * Figma-specced "floating card" popup chrome (bordered, rounded, elevated)
 * shared by every current-theme popover/dropdown-panel surface. Each
 * property already resolves to `undefined` for legacy (JSS omits an
 * `undefined` value), so it's safe to spread directly into a class body
 * without an outer `toggleThemeStyle` wrapper - a caller's own legacy
 * styling on the same class is untouched. A function (not a bare object)
 * since `dlsColor`/`dlsShadow` must be invoked lazily, at the consuming
 * style factory's own call time, not at this module's load time.
 *
 * ```typescript
 * import {dlsFloatingCardStyle} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const useStyles = makeStyles(() => ({
 *   popoverRoot : {
 *     marginLeft : toggleThemeStyle('15px', undefined),
 *     width      : toggleThemeStyle(undefined, '286px'),
 *     ...dlsFloatingCardStyle()
 *   }
 * }));
 * ```
 */
export const dlsFloatingCardStyle = () => ({
  border       : toggleThemeStyle(undefined, `1px solid ${dlsColor('grey5')}`),
  borderRadius : toggleThemeStyle(undefined, '8px'),
  boxShadow    : toggleThemeStyle(undefined, dlsShadow(4))
});

/**
 * Fixed-width tab style, pinned with `!important` so it beats MUI's own
 * base Tab styles (a non-important `minWidth`/`maxWidth`) composed onto
 * the same element. Used wherever a Figma spec calls for a tab that does
 * not size itself to its own label text.
 *
 * ```typescript
 * import {dlsFixedTabWidth} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const tabOverrides = () => ({
 *   materialRoot : dlsFixedTabWidth(252)
 * });
 * ```
 *
 * @param widthPx Fixed tab width, in pixels.
 * @returns A style object pinning `width`/`minWidth`/`maxWidth`/
 *          `flexBasis` to `widthPx` with `!important`.
 */
export const dlsFixedTabWidth = (widthPx: number) => ({
  width     : `${widthPx}px !important`,
  minWidth  : `${widthPx}px !important`,
  maxWidth  : `${widthPx}px !important`,
  flexBasis : `${widthPx}px !important`
});

/**
 * Figma "Tab Group" indicator/baseline border - 2px, `pastelTeal` at rest,
 * `darkTeal` when selected. Every element that draws this line (a tab's own
 * per-tab root, or a tab row's fill for the gaps between tabs) builds it
 * from this same pair of colors.
 *
 * ```typescript
 * import {dlsTabIndicatorBorder} from '@ion/dls/src/theme/helpers';
 * ...
 *
 * const useStyles = () => ({
 *   root : {
 *     borderBottom : dlsTabIndicatorBorder('resting'),
 *     '&$selected' : {
 *       borderBottom : dlsTabIndicatorBorder('selected')
 *     }
 *   }
 * });
 * ```
 *
 * @param state Whether the tab is `'resting'` or `'selected'`.
 * @returns The `border-bottom` value (without `!important`).
 */
export const dlsTabIndicatorBorder = (state: 'resting' | 'selected'): string =>
  `2px solid ${dlsColor(state === 'selected' ? 'darkTeal' : 'pastelTeal')}`;
